import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { goTo } from '@tiendanube/nexo';
import { ECurrency } from 'App/i18n/i18n.types';
import { ROUTES } from 'App/Routes/constants';
import { useTranslation } from 'react-i18next';
import InputMask from 'react-input-mask';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Icon,
  Input,
  Link,
  Radio,
  Sidebar,
  Text,
  Title
} from '@nimbus-ds/components';
import { ChevronLeftIcon } from '@nimbus-ds/icons';

import { currency } from 'commons/utils';
import {
  AMP_BUTTON,
  AmpActions,
  sendAmplitudeData
} from 'commons/utils/amplitude';
import { APIError } from 'commons/utils/axios/axiosApiInstanceError';
import { SideModals } from 'commons/utils/constants';
import Domain from 'commons/utils/domains';
import { convertNumberStringToFloat } from 'commons/utils/format';
import maskToCurrency from 'commons/utils/format/mask-currency';
import useAddCredit from 'commons/utils/hooks/useAddCredit';
import { useSidemodalsContext } from 'commons/utils/hooks/useModal';
import { useStoreContext } from 'commons/utils/hooks/useStoreContext';
import queryClient from 'commons/utils/query';

import { useNuvemPagoBalance } from 'domains/balance/api/get-nuvempago-balance';
import {
  EAddCredit,
  RETRY_BALANCE_COUNT
} from 'domains/balance/types/add-credit';
import {
  getDefaultValuesPerCountry,
  getMinimumAmountPerCountry
} from 'domains/balance/utils/amounts-per-country';

import nexo from 'nexoClient';

import { useBalanceContext } from '../../context/useBalanceContext';

import PaymentMethod from './payment-method/payment';
import PaymentMethodSkeleton from './payment-method/payment-skeleton';

const BalanceAddCredit: FC = () => {
  const { t } = useTranslation(Domain.BALANCE);
  const store = useStoreContext();
  const { dispatch } = useSidemodalsContext();
  const navigate = useNavigate();
  const { state, dispatch: dispatchBalance } = useBalanceContext();

  const [canRetryBalance, setCanRetryBalance] = useState(true);
  const [countRetryBalance, setCountRetryBalance] = useState(0);
  const [value, setValue] = useState<string>(
    state?.amount ? state?.amount?.toFixed(2).toString().replace('.', ',') : ''
  );

  const [isMobileLinkVisible, setIsMobileLinkVisible] = useState(false);
  const linkMobile = useRef<HTMLAnchorElement>(null);

  const currentValue = useMemo(() => {
    return convertNumberStringToFloat(value);
  }, [value]);

  const minimumAmountPrepaid = getMinimumAmountPerCountry(store.country);
  const minimumAmountError = currentValue < minimumAmountPrepaid;
  const defaultValues = getDefaultValuesPerCountry(store.country);

  const {
    data,
    isLoading: isLoadingBalance,
    refetch
  } = useNuvemPagoBalance({
    onSuccess: () => {
      if (data?.balance === 0) {
        setCanRetryBalance(false);
      }
    },
    onError: (err) => {
      const error = err as unknown as APIError;

      if (error.status === 403 || error.status === 400) {
        setCanRetryBalance(false);
      } else {
        if (countRetryBalance === RETRY_BALANCE_COUNT) {
          setCanRetryBalance(false);
        } else {
          setCanRetryBalance(true);
        }
      }
    }
  });

  const suggestionRecharge = queryClient.getQueryData<{ amount: number }>([
    'statement-suggestion'
  ]);

  const { handleAddingCredit, isLoading, handleParseUrl } = useAddCredit();

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleClickDefaultOption = (
    event: React.SyntheticEvent<EventTarget>
  ) => {
    const element = (event.currentTarget as Element).getAttribute('id');
    const increase = element || '';

    setValue(increase.split('-')[1]);
  };

  const handleClose = (): void => {
    setValue('');

    dispatch({ type: 'CLOSE_MODAL', payload: SideModals.ADDCREDIT });
    dispatchBalance({ type: 'RESET_AMOUNT' });
  };

  const handleConfirmCharge = async (type: EAddCredit) => {
    const amount = convertNumberStringToFloat(value);

    dispatchBalance({ type: 'SET_AMOUNT', payload: amount });
    sessionStorage.setItem('add-credit-amount', amount.toString());

    if (type === EAddCredit.DEFAULT) {
      sendAmplitudeData(AMP_BUTTON, {
        action: AmpActions.ADD_CREDIT_OTHER_PAYMENT_CLICK
      });

      const response = await handleAddingCredit(amount);

      if (response) {
        const url = handleParseUrl(response);
        goTo(nexo, url);
      }
    }

    if (type === EAddCredit.NUVEMPAGO) {
      if (
        canRetryBalance &&
        countRetryBalance < RETRY_BALANCE_COUNT &&
        !data?.balance
      ) {
        sendAmplitudeData(AMP_BUTTON, {
          action: AmpActions.ADD_CREDIT_NUVEM_PAGO_RELOAD
        });
        setCountRetryBalance(countRetryBalance + 1);
        refetch();

        return;
      }

      sendAmplitudeData(AMP_BUTTON, {
        action: AmpActions.ADD_CREDIT_NUVEM_PAGO_CLICK
      });

      dispatch({ type: 'CLOSE_MODAL', payload: SideModals.ADDCREDIT });

      navigate(ROUTES.BALANCE_NUVEM_PAGO);
    }
  };

  useEffect(() => {
    if (suggestionRecharge?.amount) {
      setValue(
        suggestionRecharge.amount.toFixed(2).toString().replace('.', ',')
      );

      queryClient.resetQueries(['statement-suggestion']);
    }
  }, [minimumAmountPrepaid, suggestionRecharge]);

  const isAvailableBalance = useMemo(() => {
    if (data?.balance && currentValue) {
      return Boolean(data?.balance - currentValue >= 0);
    }

    return true;
  }, [data?.balance, currentValue]);

  return (
    <Sidebar
      padding="base"
      position="right"
      open={true}
      zIndex="800"
      maxWidth={{ xs: '100%', md: '375px' }}
      onRemove={handleClose}
    >
      <Sidebar.Header>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          gap="6"
          justifyContent="flex-start"
        >
          <Link as="a" onClick={handleClose} textDecoration="none">
            <Icon color="primary-textHigh" source={<ChevronLeftIcon />} />
          </Link>

          <Title>{t('modal.add-credit.title')}</Title>
        </Box>
      </Sidebar.Header>
      <Sidebar.Body>
        <Box mt="6" display="flex" flexDirection="column" gap="2">
          <InputMask
            mask="9999999999"
            alwaysShowMask={false}
            beforeMaskedStateChange={maskToCurrency}
            onChange={handleChangeInput}
            value={value}
            maskPlaceholder=""
            id="add-credit-input"
          >
            <Input
              appendPosition="start"
              append={currency.getCurrencySymbol(
                store.language,
                store?.currency as ECurrency
              )}
              name="value"
              appearance={minimumAmountError ? 'danger' : 'neutral'}
              placeholder={t('labels.input')}
            />
          </InputMask>

          {minimumAmountError && (
            <Text fontSize="caption" color="danger-interactive">
              {t('errors.minimum-amount')}
            </Text>
          )}
        </Box>

        <Box display="flex" gap="2" mt="4">
          {defaultValues.map((amount: string, index: number) => (
            <Radio
              key={index}
              onClick={handleClickDefaultOption}
              name="default-values"
              id={`value-${amount}`}
              as="button"
              label={currency.format(
                store.language,
                store?.currency,
                parseFloat(amount)
              )}
              checked={value === amount}
              readOnly
            />
          ))}
        </Box>

        {currentValue ? (
          <Box mt="8" display="flex" flexDirection="column" gap="4">
            <Title as="h5">{t('modal.add-credit.payment-method')}</Title>

            {!data?.balance && isLoadingBalance ? (
              <>
                <PaymentMethodSkeleton />
                <PaymentMethodSkeleton />
              </>
            ) : (
              <>
                <PaymentMethod
                  type={EAddCredit.NUVEMPAGO}
                  disabled={
                    !canRetryBalance ||
                    !isAvailableBalance ||
                    minimumAmountError ||
                    data?.balance === 0
                  }
                  title={t('payment-methods.nuvem-pago.title')}
                  subtitle={t('labels.no-taxes')}
                  balance={currency.format(
                    store.language,
                    store?.currency,
                    data?.balance || 0
                  )}
                  onClick={() => handleConfirmCharge(EAddCredit.NUVEMPAGO)}
                  unavailable={!canRetryBalance}
                  isAvailableBalance={isAvailableBalance}
                  canRetryBalance={canRetryBalance && !data?.balance}
                  balanceWithoutCurrency={data?.balance}
                />
                <PaymentMethod
                  type={EAddCredit.DEFAULT}
                  title={t('payment-methods.default.title')}
                  onClick={() => handleConfirmCharge(EAddCredit.DEFAULT)}
                  disabled={!value || minimumAmountError || isLoading}
                  loading={isLoading}
                />

                {isMobileLinkVisible && (
                  <Link
                    as="a"
                    appearance="neutral-background"
                    textDecoration="none"
                    target="_blank"
                    rel="noopener noreferrer"
                    ref={linkMobile}
                    onClick={() => {
                      setIsMobileLinkVisible(false);
                      setValue('');
                    }}
                  >
                    <Text
                      fontWeight="medium"
                      color="currentColor"
                      textAlign="center"
                    >
                      {t('buttons.proceed')}
                    </Text>
                  </Link>
                )}
              </>
            )}
          </Box>
        ) : (
          ''
        )}
      </Sidebar.Body>
    </Sidebar>
  );
};

export default BalanceAddCredit;
